import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard, Building2, Wrench, ChevronLeft, ChevronRight, ArrowLeft,
  FileText, Bell, BarChart3, ShieldCheck, AlertTriangle, Settings, Sparkles
} from 'lucide-react';
import PKDashboard from './pk/PKDashboard';
import PKBinalar from './pk/PKBinalar';
import PKEkipmanlar from './pk/PKEkipmanlar';
import PKArizalar from './pk/PKArizalar';
import PKSozlesmeler from './pk/PKSozlesmeler';
import PKHakedis from './pk/PKHakedis';
import PKKontroller from './pk/PKKontroller';
import PKRaporlama from './pk/PKRaporlama';
import PKBildirimler from './pk/PKBildirimler';
import PKAyarlar from './pk/PKAyarlar';
import PKEkipmanZekasi from './pk/PKEkipmanZekasi';

const SIDEBAR_ITEMS = [
  { id: 'dashboard', label: 'Ana Panel', icon: LayoutDashboard, active: true },
  { id: 'binalar', label: 'Bina Yönetimi', icon: Building2, active: true },
  { id: 'ekipmanlar', label: 'Ekipman Yönetimi', icon: Wrench, active: true },
  { id: 'ekipman-zekasi', label: 'Ekipman Zekâsı', icon: Sparkles, active: true },
  { id: 'divider1', type: 'divider' },
  { id: 'kontroller', label: 'Periyodik Kontroller', icon: ShieldCheck, active: true },
  { id: 'arizalar', label: 'Arıza / Ticketing', icon: AlertTriangle, active: true },
  { id: 'sozlesmeler', label: 'Sözleşmeler', icon: FileText, active: true },
  { id: 'divider2', type: 'divider' },
  { id: 'hakedis', label: 'Hakediş Raporu', icon: BarChart3, active: true },
  { id: 'raporlama', label: 'Raporlama', icon: BarChart3, active: true },
  { id: 'bildirimler', label: 'Bildirimler', icon: Bell, active: true },
  { id: 'ayarlar', label: 'Ayarlar', icon: Settings, active: true }
];

const PKPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeModule, setActiveModule] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);

  const renderModule = () => {
    switch (activeModule) {
      case 'dashboard': return <PKDashboard />;
      case 'binalar': return <PKBinalar />;
      case 'ekipmanlar': return <PKEkipmanlar />;
      case 'ekipman-zekasi': return <PKEkipmanZekasi />;
      case 'kontroller': return <PKKontroller />;
      case 'arizalar': return <PKArizalar />;
      case 'sozlesmeler': return <PKSozlesmeler />;
      case 'hakedis': return <PKHakedis />;
      case 'raporlama': return <PKRaporlama />;
      case 'bildirimler': return <PKBildirimler />;
      case 'ayarlar': return <PKAyarlar />;
      default:
        return (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <Settings className="w-12 h-12 mb-4 text-gray-600" />
            <p className="text-lg font-medium">Bu modül Faz 2'de gelecek</p>
            <p className="text-sm text-gray-500 mt-1">Şu anda Dashboard, Bina ve Ekipman yönetimi aktiftir.</p>
          </div>
        );
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  // Erişim kontrolü
  if (!user?.is_admin && !user?.has_pk_access) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center">
        <div className="text-center space-y-4">
          <ShieldCheck className="w-16 h-16 text-gray-600 mx-auto" />
          <h2 className="text-xl font-bold text-white">Erişim Yetkisi Gerekli</h2>
          <p className="text-gray-400 text-sm max-w-md">PK (Periyodik Kontrol) modülüne erişim yetkiniz bulunmamaktadır. Yöneticinizden erişim yetkisi talep edin.</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#1B3A5C] hover:bg-[#2A4A6C]">Ana Sayfaya Dön</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1419] flex" data-testid="pk-page">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64'} bg-[#1B3A5C] border-r border-[#2A4A6C] flex flex-col transition-all duration-300 sticky top-0 h-screen`}>
        {/* Header */}
        <div className="p-4 border-b border-[#2A4A6C]">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#E67E22] rounded-lg flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-sm font-bold text-white leading-tight">Periyodik Kontrol</h1>
                <p className="text-[10px] text-gray-400">İSG & Tesis Yönetimi</p>
              </div>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="flex justify-center">
              <div className="w-9 h-9 bg-[#E67E22] rounded-lg flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {SIDEBAR_ITEMS.map(item => {
            if (item.type === 'divider') {
              return <div key={item.id} className="my-2 border-t border-[#2A4A6C]" />;
            }
            const Icon = item.icon;
            const isActive = activeModule === item.id;
            return (
              <button
                key={item.id}
                onClick={() => item.active && setActiveModule(item.id)}
                disabled={!item.active}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all
                  ${isActive ? 'bg-[#E67E22]/20 text-[#E67E22] font-medium' : item.active ? 'text-gray-300 hover:bg-[#2A4A6C] hover:text-white' : 'text-gray-600 cursor-not-allowed'}
                  ${sidebarCollapsed ? 'justify-center' : ''}`}
                data-testid={`pk-nav-${item.id}`}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                {!sidebarCollapsed && <span>{item.label}</span>}
                {!sidebarCollapsed && !item.active && (
                  <span className="ml-auto text-[9px] text-gray-600 bg-gray-800 px-1.5 py-0.5 rounded">Yakında</span>
                )}
              </button>
            );
          })}
        </nav>

        {/* Collapse Toggle */}
        <div className="p-2 border-t border-[#2A4A6C]">
          <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="w-full flex items-center justify-center py-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-[#2A4A6C]">
            {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top Bar */}
        <header className="bg-[#1A2332] border-b border-gray-800 px-3 sm:px-6 py-3 flex items-center justify-between gap-2 sticky top-0 z-10">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white h-8 px-2" data-testid="pk-back-btn">
              <ArrowLeft className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Ana Sayfa</span>
            </Button>
            <span className="text-gray-600">|</span>
            <span className="text-sm text-gray-300 font-medium truncate">
              {SIDEBAR_ITEMS.find(i => i.id === activeModule)?.label || 'PK'}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className="text-xs text-gray-400 hidden sm:inline">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-red-400 hover:text-red-300 h-8 text-xs">Çıkış</Button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-3 sm:p-6 overflow-x-hidden overflow-y-auto">
          {renderModule()}
        </main>
      </div>
    </div>
  );
};

export default PKPage;
